/**
 * BXGY form — Bundle-parallel cart add + non-restricted auto-GET sync.
 * Discount amount stays with Shopify (manual discount codes + Function).
 *
 * Line properties:
 * - `_bxgy_offer_id` / `_bxgy_name` — always (cart grouping, like Bundle)
 * - `_bxgy_role` — buy | get (distinguish pools; keeps same-variant lines separate)
 * - `_bxgy_rule_id` — when metaobject Rule ID is set (form ATC + auto-sync stamp on claim/top-up).
 *
 * Quantities come from metaobject `buy_quantity` / `free_quantity` (never hardcoded).
 * Optional metaobject `discount_code` is applied via cart/update after a successful add (never hardcoded).
 *
 * Non-restricted auto-sync (BXGY-page parity): on cartUpdate, immediately evaluate using
 * mutation `cartData` when it includes `items` (skip /cart.js); otherwise fetch /cart.js once.
 * Claims unmarked BUY into `_bxgy_role=buy`; adds missing `_bxgy_role=get` for diff-SKU free.
 * Same-SKU free folds into BUY qty — Shopify discount splits paid/₹0 (₹0 = free gift card).
 * No artificial setTimeout delay. Paint drawer before awaiting other cartUpdate subscribers.
 * Debug: `window.ncBxgyDebug = true` → console `[nc-bxgy]` logs.
 *
 * Restriction gate (no site-wide unmarked claim):
 * - `restrictToMarkedLineItems === true` OR non-empty `ruleId` → form/marker path only
 * - blank ruleId and flag not true → claim unmarked + auto free from any page
 *
 * GoKwik Buy Now: SDK adds lines without publishing theme cartUpdate, so the BXGY GoKwik
 * branch arms the offer code and flushes after the SDK call (short cart.js poll).
 */
let ncBxgyPendingDiscount = null;
let ncBxgyDiscountFlushTimer = null;
let ncBxgyDiscountInFlight = false;
let ncBxgyAutoGetInFlight = false;
let ncBxgyAutoGetPending = null;

function ncBxgyNormalizeDiscountCode(value) {
  return (value || '').trim();
}

function ncBxgyDiscountAlreadyApplied(cartState, code) {
  const normalized = ncBxgyNormalizeDiscountCode(code).toUpperCase();
  if (!normalized || !cartState) return false;
  return (cartState.discount_codes || []).some(
    (entry) => entry.code?.toUpperCase() === normalized && entry.applicable === true,
  );
}

function ncBxgyClearPendingDiscount() {
  ncBxgyPendingDiscount = null;
  if (ncBxgyDiscountFlushTimer) {
    clearTimeout(ncBxgyDiscountFlushTimer);
    ncBxgyDiscountFlushTimer = null;
  }
}

function ncBxgyApplyDiscountCode(code, cartElement) {
  const normalized = ncBxgyNormalizeDiscountCode(code);
  if (!normalized) return Promise.resolve(null);
  if (typeof routes === 'undefined' || typeof fetchConfig !== 'function') return Promise.resolve(null);
  if (window.ncIsFirstOrderBlocked?.(normalized)) return Promise.resolve(null);

  const body = {
    discount: normalized,
    sections_url: window.location.pathname,
  };
  if (cartElement?.getSectionsToRender) {
    body.sections = cartElement.getSectionsToRender().map((section) => section.id);
  }

  return fetch(routes.cart_update_url, {
    ...fetchConfig(),
    body: JSON.stringify(body),
  })
    .then((response) => response.json())
    .then((state) => {
      if (state.status) return null;
      if (!ncBxgyDiscountAlreadyApplied(state, normalized)) return null;
      return state;
    })
    .catch(() => null);
}

function ncBxgyCartJsonUrl() {
  const base = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
  return `${base}.js`;
}

/**
 * Arm discount for GoKwik Buy Now (called from gokwik.liquid before onBuyNowClick).
 * In-memory only — BXGY script loads on BXGY pages; GoKwik stays on the same page.
 */
window.ncBxgyArmDiscount = function (root) {
  const code = ncBxgyNormalizeDiscountCode(root?.dataset?.discountCode);
  const offerId = (root?.dataset?.bxgyId || '').trim();
  if (!code || !offerId) {
    ncBxgyClearPendingDiscount();
    return;
  }
  ncBxgyPendingDiscount = { code, offerId, attempts: 0 };
};

/**
 * After GoKwik mutates the cart, poll briefly then apply the armed code once.
 */
window.ncBxgyFlushPendingDiscount = function () {
  if (!ncBxgyPendingDiscount) return;

  const retryMs = 250;
  const maxAttempts = 12;

  if (ncBxgyDiscountInFlight) {
    if (!ncBxgyDiscountFlushTimer) {
      ncBxgyDiscountFlushTimer = setTimeout(() => {
        ncBxgyDiscountFlushTimer = null;
        window.ncBxgyFlushPendingDiscount();
      }, retryMs);
    }
    return;
  }

  const pending = ncBxgyPendingDiscount;
  pending.attempts = (pending.attempts || 0) + 1;

  ncBxgyDiscountInFlight = true;
  fetch(ncBxgyCartJsonUrl(), { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then((cart) => {
      if (!ncBxgyPendingDiscount || ncBxgyPendingDiscount !== pending) return null;

      if (ncBxgyDiscountAlreadyApplied(cart, pending.code)) {
        ncBxgyClearPendingDiscount();
        return null;
      }

      const hasOffer = (cart.items || []).some(
        (item) => item.properties && item.properties._bxgy_offer_id === pending.offerId,
      );
      if (!hasOffer) {
        if (pending.attempts < maxAttempts) {
          ncBxgyDiscountFlushTimer = setTimeout(() => {
            ncBxgyDiscountFlushTimer = null;
            window.ncBxgyFlushPendingDiscount();
          }, retryMs);
        } else {
          ncBxgyClearPendingDiscount();
        }
        return null;
      }

      // Offer lines are present — one apply attempt, then stop (invalid codes must not loop).
      // No cartElement: skip section paint / drawer open (GoKwik Buy Now is already open).
      ncBxgyClearPendingDiscount();
      return ncBxgyApplyDiscountCode(pending.code, null);
    })
    .catch(() => {
      if (ncBxgyPendingDiscount === pending && pending.attempts < maxAttempts) {
        ncBxgyDiscountFlushTimer = setTimeout(() => {
          ncBxgyDiscountFlushTimer = null;
          window.ncBxgyFlushPendingDiscount();
        }, retryMs);
      } else if (ncBxgyPendingDiscount === pending) {
        ncBxgyClearPendingDiscount();
      }
    })
    .finally(() => {
      ncBxgyDiscountInFlight = false;
    });
};

function ncBxgyLog(...args) {
  if (typeof window !== 'undefined' && window.ncBxgyDebug) {
    console.info('[nc-bxgy]', ...args);
  }
}

/**
 * Build cart line items for a BXGY offer (form + GoKwik).
 * Same-SKU free → BUY qty (buy+free); discount splits paid/₹0 free card.
 * Diff-SKU free → `_bxgy_role=get`.
 */
function ncBxgyBuildOfferItems(input) {
  const buyVariantIds = (input?.buyVariantIds || []).map(Number).filter((id) => id > 0);
  const freeVariantIds = (input?.freeVariantIds || []).map(Number).filter((id) => id > 0);
  const buyQty = Number(input?.buyQuantity);
  const freeQty = Number(input?.freeQuantity);
  const sets = Math.max(1, Number(input?.sets) || 1);
  const offerId = String(input?.offerId || '').trim();
  const offerName = String(input?.offerName || '').trim();
  const ruleId = String(input?.ruleId || '').trim();

  if (!buyVariantIds.length || !freeVariantIds.length) return [];
  if (!Number.isFinite(buyQty) || buyQty < 1 || !Number.isFinite(freeQty) || freeQty < 1) return [];
  if (!offerId) return [];

  const buyNeed = buyQty * sets;
  const freeNeed = freeQty * sets;
  const buyIdSet = new Set(buyVariantIds);

  const baseProperties = {
    _bxgy_offer_id: offerId,
    _bxgy_name: offerName,
  };
  if (ruleId) {
    baseProperties._bxgy_rule_id = ruleId;
  }

  const items = [];
  const addLine = (id, quantity, role) => {
    if (!id || quantity < 1) return;
    const properties = {
      ...baseProperties,
      _bxgy_role: role,
    };
    const existing = items.find(
      (item) => item.id === id && item.properties._bxgy_role === role,
    );
    if (existing) {
      existing.quantity += quantity;
      return;
    }
    items.push({ id, quantity, properties });
  };

  buyVariantIds.forEach((id) => {
    addLine(id, buyNeed + (freeVariantIds.includes(id) ? freeNeed : 0), 'buy');
  });
  freeVariantIds.forEach((id) => {
    if (buyIdSet.has(id)) return;
    addLine(id, freeNeed, 'get');
  });

  ncBxgyLog('build', { offerId, buyNeed, freeNeed, items });
  return items;
}

function ncBxgyLineItemsFrom(root) {
  if (!root) return [];

  const buyNodes = Array.from(root.querySelectorAll('[data-bxgy-variant]'));
  const freeNodes = Array.from(root.querySelectorAll('[data-bxgy-free]'));
  if (!buyNodes.length || !freeNodes.length) return [];
  if (buyNodes.some((node) => node.dataset.available !== 'true')) return [];
  if (freeNodes.some((node) => node.dataset.available !== 'true')) return [];

  const buyVariantIds = buyNodes.map((node) => Number(node.dataset.variantId || node.value));
  const freeVariantIds = freeNodes.map((node) => Number(node.dataset.variantId || node.value));
  const sets = Math.max(1, parseInt(root.querySelector('[name="quantity"]')?.value, 10) || 1);

  return ncBxgyBuildOfferItems({
    buyVariantIds,
    freeVariantIds,
    buyQuantity: parseInt(root.dataset.buyQuantity, 10),
    freeQuantity: parseInt(root.dataset.freeQuantity, 10),
    sets,
    offerId: root.dataset.bxgyId,
    offerName: root.dataset.bxgyName,
    ruleId: root.dataset.bxgyRuleId,
  });
}

window.ncBxgyLineItems = function (btn) {
  const root = btn && btn.closest ? btn.closest('[data-bxgy-form]') : null;
  return ncBxgyLineItemsFrom(root);
};

window.ncBxgyBuildOfferItems = ncBxgyBuildOfferItems;

/**
 * Gate for claiming unmarked BUY (site-wide auto-GET).
 * Restricted when `restrictToMarkedLineItems === true` OR metaobject Rule ID is set.
 * Restricted offers still sync GET for lines whose `_bxgy_rule_id` matches.
 */
function ncBxgyOfferRestrictsToMarked(offer) {
  if (!offer) return true;
  if (offer.restrictToMarkedLineItems === true) return true;
  return Boolean(String(offer.ruleId || '').trim());
}

window.ncBxgyOfferRestrictsToMarked = ncBxgyOfferRestrictsToMarked;

function ncBxgyLineProps(line) {
  return line?.properties || {};
}

function ncBxgyLineVariantId(line) {
  return Number(line?.variant_id != null ? line.variant_id : line?.id) || 0;
}

/** Shopify may return 0 as number or string after discount splits. */
function ncBxgyLineIsZeroPrice(line) {
  return Number(line?.final_line_price) === 0;
}

function ncBxgyBaseOfferProperties(offer) {
  return {
    _bxgy_offer_id: String(offer.id || '').trim(),
    _bxgy_name: String(offer.name || '').trim(),
  };
}

/**
 * Plan cart mutations so normal-ATC BXGY matches BXGY-page identity:
 * - Rule ID blank: claim unmarked BUY + add free when threshold met
 * - Rule ID set: no unmarked claim; only lines with matching `_bxgy_rule_id` count; still add/trim free
 * - Diff-SKU GET / same-SKU ₹0 BUY free; legacy same-SKU GET → fold into BUY
 *
 * @returns {{ updates: Record<string, number>, items: Array<{id:number,quantity:number,properties:Record<string,string>}>, touchedOfferIds: string[] }}
 */
function ncBxgyPlanAutoGetAdditions(cart, offers) {
  const lines = cart?.items || [];
  const updates = {};
  const items = [];
  const touchedOfferIds = [];

  const unmarkedRemaining = new Map();
  for (const line of lines) {
    const key = line.key;
    if (!key) continue;
    const props = ncBxgyLineProps(line);
    if (String(props._bxgy_offer_id || '').trim()) continue;
    if (String(props._bxgy_role || '').trim()) continue;
    const qty = Number(line.quantity) || 0;
    if (qty < 1) continue;
    unmarkedRemaining.set(key, qty);
  }

  for (const offer of offers || []) {
    if (!offer) continue;

    const offerId = String(offer.id || '').trim();
    const offerRuleId = String(offer.ruleId || '').trim();
    const buyQty = Number(offer.buyQuantity);
    const freeQty = Number(offer.freeQuantity);
    const buyIds = new Set((offer.buyVariantIds || []).map(Number).filter((id) => id > 0));
    const freeIds = [...new Set((offer.freeVariantIds || []).map(Number).filter((id) => id > 0))];

    if (!offerId || !buyIds.size || !freeIds.length) continue;
    if (!Number.isFinite(buyQty) || buyQty < 1 || !Number.isFinite(freeQty) || freeQty < 1) continue;

    // Gate: blank Rule ID → site-wide unmarked claim; Rule ID set → matching `_bxgy_rule_id` only.
    const restricted = ncBxgyOfferRestrictsToMarked(offer);
    const isSameSku = freeIds.some((id) => buyIds.has(id));

    const lineMatchesRule = (props) =>
      !offerRuleId || String(props._bxgy_rule_id || '').trim() === offerRuleId;

    let markedBuy = 0;
    let freeBuyHave = 0;
    const freeBuyLines = [];
    const paidBuyLines = [];
    const getLines = [];
    const claimCandidates = [];
    let foldSameSkuGet = 0;

    for (const line of lines) {
      const variantId = ncBxgyLineVariantId(line);
      const qty = Number(line.quantity) || 0;
      if (!variantId || qty < 1) continue;
      const props = ncBxgyLineProps(line);
      const role = String(props._bxgy_role || '').trim();
      const lineOfferId = String(props._bxgy_offer_id || '').trim();

      if (role === 'get') {
        if (lineOfferId === offerId && freeIds.includes(variantId) && line.key && lineMatchesRule(props)) {
          getLines.push({ key: line.key, variantId, qty });
        }
        continue;
      }

      if (lineOfferId === offerId && role === 'buy' && buyIds.has(variantId)) {
        if (!lineMatchesRule(props)) continue;
        if (ncBxgyLineIsZeroPrice(line) && line.key) {
          freeBuyLines.push({ key: line.key, variantId, qty });
          freeBuyHave += qty;
          continue;
        }
        if (line.key) paidBuyLines.push({ key: line.key, variantId, qty });
        markedBuy += qty;
        continue;
      }

      if (restricted || lineOfferId) continue;
      if (!buyIds.has(variantId)) continue;
      const key = line.key;
      if (!key) continue;
      const available = unmarkedRemaining.get(key) || 0;
      if (available < 1) continue;
      claimCandidates.push({ key, variantId, available });
    }

    // Restricted + nothing stamped for this offer → skip (normal ATC must not grow qty).
    if (restricted && markedBuy < 1 && freeBuyHave < 1 && !getLines.length) continue;

    // Same-SKU: discount often leaves paid2 + paid1 + free1 — merge paid into one line for correct qty 3.
    if (isSameSku && paidBuyLines.length > 1) {
      const byVariant = new Map();
      for (const row of paidBuyLines) {
        const list = byVariant.get(row.variantId) || [];
        list.push(row);
        byVariant.set(row.variantId, list);
      }
      for (const group of byVariant.values()) {
        if (group.length < 2) continue;
        let total = 0;
        for (const row of group) total += row.qty;
        updates[group[0].key] = total;
        for (let i = 1; i < group.length; i++) updates[group[i].key] = 0;
        touchedOfferIds.push(offerId);
      }
    }

    const unmarkedBuy = restricted ? 0 : claimCandidates.reduce((sum, row) => sum + row.available, 0);
    const sets = Math.floor((markedBuy + unmarkedBuy) / buyQty);
    const baseFreeNeed = sets < 1 ? 0 : freeQty * sets;
    const remainingGetNeed = new Map(
      freeIds.filter((id) => !buyIds.has(id)).map((id) => [id, baseFreeNeed]),
    );
    const baseProperties = ncBxgyBaseOfferProperties(offer);
    if (offerRuleId) baseProperties._bxgy_rule_id = offerRuleId;
    let touched = false;
    let sameSkuFreeNeed = 0;
    for (const freeId of freeIds) {
      if (buyIds.has(freeId)) sameSkuFreeNeed += baseFreeNeed;
    }

    ncBxgyLog('plan:offer', {
      offerId,
      restricted,
      offerRuleId,
      markedBuy,
      freeBuyHave,
      unmarkedBuy,
      sets,
      getNeed: Object.fromEntries(remainingGetNeed),
    });

    for (const getLine of getLines) {
      if (buyIds.has(getLine.variantId)) {
        updates[getLine.key] = 0;
        if (sets >= 1) foldSameSkuGet += getLine.qty;
        touched = true;
        continue;
      }
      const need = remainingGetNeed.get(getLine.variantId) || 0;
      const keep = Math.min(getLine.qty, need);
      remainingGetNeed.set(getLine.variantId, need - keep);
      if (keep !== getLine.qty) {
        updates[getLine.key] = keep;
        touched = true;
      }
    }

    let freeBuyKeep = sets < 1 ? 0 : sameSkuFreeNeed;
    for (const freeBuy of freeBuyLines) {
      const keep = Math.min(freeBuy.qty, freeBuyKeep);
      freeBuyKeep -= keep;
      if (keep !== freeBuy.qty) {
        updates[freeBuy.key] = keep;
        touched = true;
      }
    }

    if (foldSameSkuGet > 0) {
      const foldId = freeIds.find((id) => buyIds.has(id)) || [...buyIds][0];
      if (foldId) {
        items.push({
          id: foldId,
          quantity: foldSameSkuGet,
          properties: { ...baseProperties, _bxgy_role: 'buy' },
        });
      }
    }

    if (sets < 1) {
      if (touched) touchedOfferIds.push(offerId);
      continue;
    }

    // Pool shortfall: claim unmarked only when unrestricted; same-SKU free only with paid BUY pool.
    let poolShort = Math.max(0, sets * buyQty + sameSkuFreeNeed - markedBuy - freeBuyHave - foldSameSkuGet);
    let claimedBuy = 0;

    if (!restricted) {
      for (const candidate of claimCandidates) {
        if (poolShort < 1) break;
        const remaining = unmarkedRemaining.get(candidate.key) || 0;
        if (remaining < 1) continue;
        const take = Math.min(remaining, poolShort);
        if (take < 1) continue;

        const nextQty = remaining - take;
        unmarkedRemaining.set(candidate.key, nextQty);
        updates[candidate.key] = nextQty;
        items.push({
          id: candidate.variantId,
          quantity: take,
          properties: {
            ...baseProperties,
            _bxgy_role: 'buy',
          },
        });
        poolShort -= take;
        claimedBuy += take;
        touched = true;
      }
    }

    // Same-SKU free top-up only when BUY pool exists (marked or just claimed) — never unmarked-only invent.
    if (poolShort > 0 && sameSkuFreeNeed > 0 && markedBuy + claimedBuy > 0) {
      const foldId = freeIds.find((id) => buyIds.has(id));
      if (foldId) {
        items.push({
          id: foldId,
          quantity: poolShort,
          properties: { ...baseProperties, _bxgy_role: 'buy' },
        });
        poolShort = 0;
        touched = true;
      }
    }

    for (const freeId of freeIds) {
      if (buyIds.has(freeId)) continue;
      const missing = remainingGetNeed.get(freeId) || 0;
      if (missing < 1) continue;
      items.push({
        id: freeId,
        quantity: missing,
        properties: {
          ...baseProperties,
          _bxgy_role: 'get',
        },
      });
      touched = true;
    }

    if (touched) touchedOfferIds.push(offerId);
    ncBxgyLog('plan:result', { offerId, updates, items, foldSameSkuGet, touched });
  }

  return { updates, items, touchedOfferIds };
}

window.ncBxgyPlanAutoGetAdditions = ncBxgyPlanAutoGetAdditions;

function ncBxgyReadOfferCatalog() {
  if (Array.isArray(window.ncBxgyOffers)) return window.ncBxgyOffers;
  const el = typeof document !== 'undefined' ? document.getElementById('nc-bxgy-offers-data') : null;
  if (!el) {
    window.ncBxgyOffers = [];
    return window.ncBxgyOffers;
  }
  try {
    const parsed = JSON.parse(el.textContent || '[]');
    window.ncBxgyOffers = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    window.ncBxgyOffers = [];
  }
  return window.ncBxgyOffers;
}

function ncBxgyPaintCartAfterAutoGet(state, cartEl) {
  if (!cartEl || !state?.sections || typeof cartEl.renderContents !== 'function') return;
  // Paint immediately — do not await publish (other subscribers may fetch sections).
  cartEl.renderContents(state);
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'bxgy-auto-get',
      cartData: state,
    });
  }
}

/** Live section HTML from current cart (post-discount same-SKU split can stale mutation sections). */
function ncBxgyRefreshCartSections(cartEl) {
  if (!cartEl || typeof routes === 'undefined' || typeof fetchConfig !== 'function') {
    return Promise.resolve(null);
  }
  const sectionIds = cartEl.getSectionsToRender?.().map((section) => section.id);
  if (!sectionIds?.length) return Promise.resolve(null);
  return fetch(routes.cart_update_url, {
    ...fetchConfig(),
    body: JSON.stringify({
      sections: sectionIds,
      sections_url: window.location.pathname,
    }),
  })
    .then((response) => response.json())
    .then((state) => {
      if (state?.status || !state?.sections) return null;
      if (!ncBxgyAutoGetPending) ncBxgyPaintCartAfterAutoGet(state, cartEl);
      return state;
    })
    .catch(() => null);
}

function ncBxgyCartStateUsable(cart) {
  if (!cart || !Array.isArray(cart.items)) return false;
  if (typeof cart.item_count !== 'number') return false;
  let sum = 0;
  for (const line of cart.items) {
    if (!line?.key) return false;
    sum += Number(line.quantity) || 0;
  }
  return sum === cart.item_count;
}

window.ncBxgyCartStateUsable = ncBxgyCartStateUsable;

function ncBxgyResolveCartState(cartHint) {
  if (ncBxgyCartStateUsable(cartHint)) return Promise.resolve(cartHint);
  return fetch(ncBxgyCartJsonUrl(), { headers: { Accept: 'application/json' } }).then((response) =>
    response.json(),
  );
}

/**
 * Claim unmarked BUY into BXGY buy lines + add missing GET (BXGY-page parity).
 * @param {object|null} [cartHint] Mutation response with `items` when available (skip /cart.js).
 */
function ncBxgySyncAutoGet(cartHint) {
  if (ncBxgyAutoGetInFlight) {
    ncBxgyAutoGetPending = true;
    return Promise.resolve(null);
  }
  const offers = ncBxgyReadOfferCatalog();
  if (!offers.length) return Promise.resolve(null);
  if (typeof routes === 'undefined') return Promise.resolve(null);

  ncBxgyAutoGetInFlight = true;
  return ncBxgyResolveCartState(cartHint)
    .then((cart) => {
      const plan = ncBxgyPlanAutoGetAdditions(cart, offers);
      const updateKeys = Object.keys(plan.updates || {});
      if (!plan.items.length && !updateKeys.length) return null;

      const cartEl =
        typeof document !== 'undefined'
          ? document.querySelector('cart-drawer') || document.querySelector('cart-notification')
          : null;
      const sectionIds = cartEl?.getSectionsToRender
        ? cartEl.getSectionsToRender().map((section) => section.id)
        : null;
      const attachSections = (body) => {
        if (!sectionIds) return body;
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
        return body;
      };

      const applyPlan = (nextPlan) => {
        const keys = Object.keys(nextPlan.updates || {});
        const runUpdates = () => {
          if (!keys.length) return Promise.resolve(null);
          if (typeof fetchConfig !== 'function') return Promise.resolve(null);
          return fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify(attachSections({ updates: nextPlan.updates })),
          })
            .then((response) => response.json())
            .then((state) => (state?.status || state?.errors ? null : state))
            .catch(() => null);
        };
        const runAdds = () => {
          if (!nextPlan.items.length) return Promise.resolve(null);
          return fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(attachSections({ items: nextPlan.items })),
          })
            .then((response) => response.json())
            .then((state) => (state?.status ? null : state));
        };
        return runUpdates().then((updateState) => {
          if (keys.length && !updateState) return null;
          return runAdds().then((addState) => {
            if (!addState && nextPlan.items.length) return null;
            const next = addState || updateState;
            if (next && updateState?.sections && !next.sections) next.sections = updateState.sections;
            if (next && addState?.sections) next.sections = addState.sections;
            return next;
          });
        });
      };

      return applyPlan(plan)
        .then((state) => {
          if (!state && !plan.items.length) return null;

          const codes = [];
          for (const offer of offers) {
            if (ncBxgyOfferRestrictsToMarked(offer)) continue;
            const code = ncBxgyNormalizeDiscountCode(offer.discountCode);
            if (!code) continue;
            if (!plan.touchedOfferIds.includes(String(offer.id))) continue;
            codes.push(code);
          }

          const applyNext = (index, latest) => {
            if (index >= codes.length) return Promise.resolve(latest);
            return ncBxgyApplyDiscountCode(codes[index], cartEl).then((next) =>
              applyNext(index + 1, next || latest),
            );
          };

          // One paint after discount + same-SKU paid-line merge (discount splits paid3 → paid2+paid1).
          return applyNext(0, state).then((discounted) => {
            const latest = discounted || state;
            return ncBxgyResolveCartState(latest).then((cart) => {
              const fix = ncBxgyPlanAutoGetAdditions(cart, offers);
              const fixKeys = Object.keys(fix.updates || {});
              if (!fixKeys.length && !fix.items.length) {
                if (latest?.sections && !ncBxgyAutoGetPending) {
                  ncBxgyPaintCartAfterAutoGet(latest, cartEl);
                  return latest;
                }
                return ncBxgyRefreshCartSections(cartEl).then((s) => s || latest);
              }
              return applyPlan(fix).then((fixed) => {
                const out = fixed || latest;
                if (out?.sections && !ncBxgyAutoGetPending) {
                  ncBxgyPaintCartAfterAutoGet(out, cartEl);
                  return out;
                }
                return ncBxgyRefreshCartSections(cartEl).then((s) => s || out);
              });
            });
          });
        });
    })
    .catch(() => null)
    .finally(() => {
      ncBxgyAutoGetInFlight = false;
      const pending = ncBxgyAutoGetPending;
      ncBxgyAutoGetPending = null;
      if (pending) ncBxgySyncAutoGet(null);
    });
}

window.ncBxgySyncAutoGet = ncBxgySyncAutoGet;

function ncBxgyInitAutoGet() {
  if (typeof window === 'undefined' || window.__ncBxgyAutoGetInit) return;
  window.__ncBxgyAutoGetInit = true;

  // Initial pass (e.g. landing with BUY already in cart). Skip empty-cart /cart.js.
  if (typeof document === 'undefined' || !document.querySelector('cart-drawer.is-empty')) {
    ncBxgySyncAutoGet();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Ignore our own paint + BXGY-form adds (already page-complete); planner is idempotent.
      if (event?.source === 'bxgy-auto-get' || event?.source === 'bxgy-form') return;
      // Immediate: reuse mutation cartData.items when present (cart/update, cart/change).
      ncBxgySyncAutoGet(event?.cartData);
    });
  }
}

function ncBxgyFormatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const noDecimals = String(Math.round(cents / 100));
  const comma = amount.replace('.', ',');
  return (format || '{{amount}}')
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
    .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace('.', "'"))
    .replace(/\{\{\s*amount\s*\}\}/g, amount);
}

if (typeof customElements !== 'undefined' && !customElements.get('bxgy-form')) {
  customElements.define(
    'bxgy-form',
    class BxgyForm extends HTMLElement {
      connectedCallback() {
        if (this.__ncBxgyBound) return;
        this.__ncBxgyBound = true;
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        const qty = this.querySelector('[name="quantity"]');
        if (qty) qty.addEventListener('change', () => this.updateTotal());
        const buyNow = this.querySelector('.nc-buy-now');
        if (buyNow) {
          buyNow.addEventListener('click', (event) => {
            if (buyNow.classList.contains('gokwik-marge') || buyNow.dataset.gokwikProcessed) return;
            event.preventDefault();
            this.addOffer(true);
          });
        }
        this.updateTotal();
      }

      updateTotal() {
        const scope = this.closest('.product') || this.closest('[data-bxgy-card]') || this;
        const total = scope.querySelector('[data-bxgy-total]');
        if (!total) return;
        const qty = Math.max(1, parseInt(this.querySelector('[name="quantity"]')?.value, 10) || 1);
        const format = this.dataset.moneyFormat;
        const unit = Number(this.dataset.price || 0);
        const compareUnit = Number(this.dataset.compare || 0);
        total.textContent = ncBxgyFormatMoney(unit * qty, format);
        const compare = scope.querySelector('[data-bxgy-compare]');
        if (compare && compareUnit > 0) compare.textContent = ncBxgyFormatMoney(compareUnit * qty, format);
        const save = scope.querySelector('[data-bxgy-save]');
        if (save && compareUnit > unit) {
          const saved = (compareUnit - unit) * qty;
          const percent = Math.round((saved * 100) / (compareUnit * qty));
          save.textContent = `Save ${ncBxgyFormatMoney(saved, format)} (${percent}%)`;
        }
      }

      showError(message) {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const text = this.querySelector('.product-form__error-message');
        if (!wrapper || !text) return;
        wrapper.hidden = !message;
        if (message) text.textContent = message;
      }

      onSubmit(event) {
        event.preventDefault();
        this.addOffer(false);
      }

      finishAfterAdd(response, goCheckout) {
        // ATC / native Buy Now own the discount path — cancel any GoKwik pending arm.
        ncBxgyClearPendingDiscount();

        const code = ncBxgyNormalizeDiscountCode(this.dataset.discountCode);
        const applyDiscount = () => {
          if (!code || ncBxgyDiscountAlreadyApplied(response, code)) {
            return Promise.resolve(response);
          }
          return ncBxgyApplyDiscountCode(code, this.cart).then((state) => state || response);
        };

        return applyDiscount().then((finalState) =>
          // Sync after discount: same-SKU ₹0 BUY ghosts + missing GET (subscribe ignores bxgy-form).
          Promise.resolve(ncBxgySyncAutoGet(finalState)).then((synced) => {
            const state = synced || finalState;
            this.error = false;
            if (goCheckout) {
              window.location.href = '/checkout';
              return;
            }
            if (!this.cart || !state.sections) {
              window.location = window.routes.cart_url;
              return;
            }
            if (synced) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bxgy-form', cartData: state });
              }
              return;
            }
            const paint = () => this.cart.renderContents(state);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              return publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bxgy-form',
                cartData: state,
              }).then(paint);
            }
            paint();
          }),
        );
      }

      addOffer(goCheckout) {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const items = ncBxgyLineItemsFrom(this);
        if (!items.length) {
          this.showError(this.dataset.unavailable || 'This offer is unavailable.');
          return;
        }

        this.showError(false);
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const body = { items };
        if (this.cart && !goCheckout) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || 'Could not add this offer to cart.');
              return;
            }
            return this.finishAfterAdd(response, goCheckout);
          })
          .catch(() => {
            this.showError('Could not add this offer to cart.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }
    },
  );
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ncBxgyInitAutoGet, { once: true });
  } else {
    ncBxgyInitAutoGet();
  }
}
